import { Component, inject, Input, OnChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PopoverController } from '@ionic/angular';
import { fromEvent, startWith, map } from 'rxjs';

@Component({
    standalone: false,
    selector: 'wl-menu',
    template: `
        <ion-list *ngIf="style === 'list'">
            <ng-container *ngFor="let item of menuItems; let i = index">
                @if (!item.hide) {
                    <ion-item-divider *ngIf="item.children" (click)="showMenu($event, item.children)">
                        {{ item.title | translate }}
                    </ion-item-divider>
                    <wl-menu
                        *ngIf="layout === 'vertical' && item.children"
                        [isLoggedIn]="isLoggedIn"
                        [menuItems]="item.children"
                        [mode]="mode"
                        [isChild]="true"
                    >
                    </wl-menu>
                    <ion-menu-toggle auto-hide="false" *ngIf="!item.children && mode === 'root'">
                        <ng-template [ngTemplateOutlet]="menuTemplate"></ng-template>
                    </ion-menu-toggle>

                    <ng-container *ngIf="mode !== 'root'">
                        <ng-template [ngTemplateOutlet]="menuTemplate"></ng-template>
                    </ng-container>

                    <ng-template #menuTemplate>
                        <ion-item
                            *ngIf="item.url"
                            [class.alt]="i % 2 === 0"
                            [routerDirection]="mode"
                            [routerLink]="[item.url]"
                            lines="full"
                        >
                            <ion-icon slot="start" [name]="item.icon"></ion-icon>
                            <ion-label *ngIf="isChild || layout === 'vertical'">
                                {{ item.title | translate }}
                            </ion-label>
                        </ion-item>

                        <ion-item
                            *ngIf="item.onClick"
                            button="true"
                            [class.alt]="i % 2 === 0"
                            [routerDirection]="mode"
                            (click)="item.onClick()"
                            lines="full"
                        >
                            <ion-icon slot="start" [name]="item.icon"></ion-icon>
                            <ion-label *ngIf="isChild || layout === 'vertical'">
                                {{ item.title | translate }}
                            </ion-label>
                        </ion-item>
                    </ng-template>
                }
            </ng-container>
        </ion-list>

        <ul class="topMenu" [class]="layout" *ngIf="style === 'menu'">
            <li *ngFor="let item of menuItems; let i = index" class="ion-activatable" [class.placeholder]="item.placeholder">
                <!-- <ion-ripple-effect></ion-ripple-effect> -->
                @if (!item.hide) {
                    <a *ngIf="item.children" [routerLink]="[item.children[0].url]">
                        <span>{{ item.title | translate }}</span>
                    </a>
                    <a *ngIf="item.url" [routerLink]="[item.url]">
                        <span><ion-icon [name]="item.icon"></ion-icon> {{ item.title | translate }}</span>
                    </a>
                    <a *ngIf="item.onClick" (click)="item.onClick()">
                        <span><ion-icon [name]="item.icon"></ion-icon> {{ item.title | translate }}</span>
                    </a>

                    <wl-menu
                        *ngIf="item.children"
                        [style]="style"
                        [isLoggedIn]="isLoggedIn"
                        [menuItems]="item.children"
                        layout="vertical"
                    ></wl-menu>
                }
            </li>
        </ul>
    `,
    styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnChanges {
    popoverController = inject(PopoverController);

    @Input()
    menuItems: MenuItem[];

    @Input()
    isLoggedIn = false;

    @Input()
    roles: string[] = [];

    @Input()
    isChild = false;

    @Input()
    mode: 'forward' | 'root' = 'forward';

    @Input()
    layout: 'vertical' | 'horizontal' = 'vertical';

    @Input()
    style: 'menu' | 'list' = 'list';

    windowSize = toSignal(
        fromEvent(window, 'resize').pipe(
            startWith(window.innerWidth),
            map(() => window.innerWidth)
        )
    );

    ngOnChanges() {
        this.menuItems = this.menuItems.filter((item) => {
            let hide = false;
            if (item.requiresLogin && !this.isLoggedIn) {
                hide = true;
            }

            if (item.requiredRoles && item.requiredRoles.length > 0) {
                hide = !this.roles.some((role) => item.requiredRoles.includes(role));
            }

            return !hide;
        });
        if (this.layout === 'vertical') {
            this.menuItems = this.menuItems.filter((x) => x.placeholder !== true);
        }
    }

    async showMenu(ev: any, menuItems: MenuItem[]) {
        if (this.layout === 'vertical') {
            return;
        }
        const popover = await this.popoverController.create({
            component: MenuComponent,
            componentProps: {
                menuItems,
                isLoggedIn: this.isLoggedIn,
                mode: this.mode,
                layout: 'vertical'
            },
            event: ev,
            translucent: true
        });
        return await popover.present();
    }
}

export interface MenuItem {
    title: string;
    icon?: string;
    url?: string;
    onClick?: () => void;
    children?: MenuItem[];
    requiresLogin?: boolean;
    requiredRoles?: string[];
    route?: string;
    placeholder?: boolean; // Only used in the horizontal layout
    hide?: boolean;
}
